import * as React from 'react';

export type UseFlowKeyboardShortcutsOptions = {
  enabled?: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onCopy: () => void;
  onPaste: (cursorPosition: { x: number; y: number } | undefined) => void;
  onCut: () => void;
  onDelete?: () => void;
  canUndo: boolean;
  canRedo: boolean;
  hasSelection: boolean;
};

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

export const useFlowKeyboardShortcuts = ({
  enabled = true,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  onCut,
  onDelete,
  canUndo,
  canRedo,
  hasSelection,
}: UseFlowKeyboardShortcutsOptions) => {
  // Track cursor position for paste
  const cursorPositionRef = React.useRef<{ x: number; y: number } | undefined>(
    undefined,
  );

  React.useEffect(() => {
    if (!enabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      cursorPositionRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if typing in input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Undo: Ctrl/Cmd + Z (no shift)
      if (modifier && e.key === 'z' && !e.shiftKey && canUndo) {
        e.preventDefault();
        onUndo();
        return;
      }

      // Redo: Ctrl/Cmd + Shift + Z OR Ctrl/Cmd + Y
      if (
        (modifier && e.key === 'z' && e.shiftKey) ||
        (modifier && e.key === 'y')
      ) {
        if (canRedo) {
          e.preventDefault();
          onRedo();
        }
        return;
      }

      // Copy: Ctrl/Cmd + C
      if (modifier && e.key === 'c' && hasSelection) {
        e.preventDefault();
        onCopy();
        return;
      }

      // Paste: Ctrl/Cmd + V
      if (modifier && e.key === 'v') {
        e.preventDefault();
        onPaste(cursorPositionRef.current);
        return;
      }

      // Cut: Ctrl/Cmd + X
      if (modifier && e.key === 'x' && hasSelection) {
        e.preventDefault();
        onCut();
        return;
      }

      // Delete: Backspace or Delete
      if (
        (e.key === 'Backspace' || e.key === 'Delete') &&
        hasSelection &&
        onDelete
      ) {
        e.preventDefault();
        onDelete();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [
    enabled,
    onUndo,
    onRedo,
    onCopy,
    onPaste,
    onCut,
    onDelete,
    canUndo,
    canRedo,
    hasSelection,
  ]);
};
